import React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { MaterialUICore, Paper, Button, Box } from '@iclinic/design-system';
import { ErrorAlert } from 'shared/components';
import { PaymentType } from 'features/onlinePayment/state/constants';
import {
  toggleTransactionDetailOpen,
  fetchTransactionDetail,
} from 'features/onlinePayment/state/reports/transactions';
import { getTransactionsListSelector } from 'features/onlinePayment/state/reports/transactions/selectors';
import useStyles from './Table.styles';
import Paginate from '../Paginate';
import NoTransactionsToView from './NoTransactionsToView';
import Loader from './Loader';
import StatusTransactionIcon from './StatusTransactionIcon';

const {
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
} = MaterialUICore;

type OnlineTransactionsTableProps = {
  isShowDetailButton?: boolean;
};

export default function OnlineTransactionsTable({
  isShowDetailButton = true,
}: OnlineTransactionsTableProps): JSX.Element {
  const classes = useStyles();
  const dispatch = useDispatch();

  const {
    fetching,
    transactionsList,
    transactionsListFetchError,
  } = useSelector(getTransactionsListSelector);

  const toggleDrawer = (id: string) => {
    dispatch(toggleTransactionDetailOpen(true));
    dispatch(fetchTransactionDetail(id));
  };

  if (transactionsList && transactionsList.length === 0) {
    return <NoTransactionsToView />;
  }

  return (
    <>
      {fetching && <Loader />}
      {!fetching && transactionsList && (
        <Paper variant="outlined" className={classes.paper}>
          <TableContainer className={classes.tableContainer}>
            <Table
              aria-label="Tabela de histórico de transações online"
              data-testid="online-transactions-table"
            >
              <TableHead>
                <TableRow>
                  <TableCell align="center">Status</TableCell>
                  <TableCell align="left">Data</TableCell>
                  <TableCell align="left">Valor</TableCell>
                  <TableCell align="left">Nome do paciente</TableCell>
                  <TableCell align="left">Condição</TableCell>
                  {isShowDetailButton && <TableCell align="center" />}
                </TableRow>
              </TableHead>
              <TableBody>
                {transactionsList &&
                  transactionsList.map((transaction) => (
                    <TableRow className={classes.tableRow} key={transaction.id}>
                      <TableCell align="center">
                        <StatusTransactionIcon status={transaction.status} />
                      </TableCell>
                      <TableCell align="left">
                        {transaction.createdAt}
                      </TableCell>
                      <TableCell align="left">{transaction.amount}</TableCell>
                      <TableCell align="left">
                        {transaction.patientName}
                      </TableCell>
                      <TableCell align="left">
                        {transaction.type === PaymentType.Credit &&
                          `Crédito em ${transaction.installmentsNumber}x`}
                        {transaction.type === PaymentType.Pix &&
                          'Transação PIX'}
                        {transaction.type === null && '-'}
                      </TableCell>
                      {isShowDetailButton && (
                        <TableCell align="center">
                          <Button
                            color="transparent"
                            onClick={() => toggleDrawer(transaction.id)}
                            size="small"
                          >
                            Ver mais
                          </Button>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Paginate />
        </Paper>
      )}
      {transactionsListFetchError && (
        <Box mt={3}>
          <ErrorAlert
            message={
              <span
                dangerouslySetInnerHTML={{
                  __html: transactionsListFetchError,
                }}
              />
            }
          />
        </Box>
      )}
    </>
  );
}
